import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Calendar } from '../../components/ui/calendar';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const REPS_BY_BRANCH = {
  equity_estate: ['Richard Demichele', 'Team HCF Property'],
  real_estate: ['Richard Demichele', 'Team HCF Property'],
};

export default function BookingPage() {
  const [params] = useSearchParams();
  const branch = params.get('branch') || 'equity_estate';
  const repParam = (params.get('rep') || '').trim().toLowerCase(); // ★ Fase 45 — preselezione rep (es. ?rep=richard)
  const [reps, setReps] = useState([]);
  const [date, setDate] = useState(new Date());
  const [slot, setSlot] = useState('');
  const [rep, setRep] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get('/public/reps').then((r) => {
      const list = r.data || [];
      setReps(list);
      // ★ Fase 45 — se arriva ?rep=<id|nome>, preseleziona il rep corrispondente
      // (es. /it/booking?rep=richard → Richard Demichele, CTA dalla chat funnel).
      if (repParam) {
        const match = list.find(
          (x) => x.id === repParam || (x.name || '').toLowerCase().includes(repParam),
        );
        if (match) setRep(match.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slots = ['10:00', '11:00', '12:00', '14:30', '15:30', '17:00'];

  const submit = async () => {
    if (!form.name || !form.email || !rep || !slot) {
      toast.error('Completa tutti i campi e seleziona uno slot');
      return;
    }
    const start = new Date(date);
    const [h, m] = slot.split(':').map(Number);
    start.setHours(h, m, 0, 0);
    try {
      const r = await api.post('/public/booking', {
        rep_id: rep, start_at: start.toISOString(), duration_minutes: 30,
        name: form.name, email: form.email, phone: form.phone, notes: form.notes, branch,
      });
      setDone(r.data);
      toast.success('Prenotazione confermata');
    } catch (e) { toast.error('Errore prenotazione'); }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-[var(--surface-1)]">
        <Card className="max-w-lg w-full border-border" data-testid="booking-done">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-[hsl(var(--primary))] mx-auto" />
            <div className="font-serif text-2xl mt-3 text-[hsl(var(--primary))]">Prenotazione confermata</div>
            <p className="text-sm text-muted-foreground mt-2">Riceverai a breve email e Telegram di conferma.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-2)]">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← Home</Link>
        <h1 className="font-serif text-3xl md:text-4xl text-[hsl(var(--primary))] mt-4" data-testid="booking-title">Prenota una call</h1>
        <p className="text-sm text-muted-foreground">Branch: <b>{branch === 'equity_estate' ? 'Equity Estate' : 'Real Estate'}</b></p>
        <Card className="mt-5 border-border">
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Seleziona la data</Label>
              <div className="mt-2 border border-border rounded-md inline-block">
                <Calendar mode="single" selected={date} onSelect={(d) => setDate(d || new Date())} className="rounded-md" data-testid="booking-calendar" />
              </div>
              <Label className="mt-4 block">Slot</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {slots.map((s) => (
                  <button key={s} onClick={() => setSlot(s)} className={`px-3 py-1.5 rounded-full border text-sm ${slot === s ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'border-border bg-card'}`} data-testid={`booking-slot-${s}`}>{s}</button>
                ))}
              </div>
              <Label className="mt-4 block">Sales rep</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(reps.length ? reps.map((r) => ({ id: r.id, name: r.name })) : (REPS_BY_BRANCH[branch] || []).map((n) => ({ id: n, name: n }))).map((r) => (
                  <button key={r.id} onClick={() => setRep(r.id)} className={`px-3 py-1.5 rounded-full border text-sm ${rep === r.id ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'border-border bg-card'}`} data-testid={`booking-rep-${r.id}`}>{r.name}</button>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Nome*</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-name" /></div>
              <div className="space-y-1.5"><Label>Email*</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-email" /></div>
              <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="booking-phone" /></div>
              <div className="space-y-1.5"><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes" /></div>
              <Button className="w-full" onClick={submit} data-testid="booking-submit">Conferma prenotazione</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
